export type NewsCategoryLabels = Record<string, string>;

type DatasourceEntry = {
  value: string;
  dimension_value?: string | null;
};

export const NEWS_CATEGORY_CACHE_SECONDS = 300;
const emptyLabels: NewsCategoryLabels = {};
const cache = new Map<string, { labels: NewsCategoryLabels; expiresAt: number }>();
const pending = new Map<string, Promise<NewsCategoryLabels>>();

const newsComponents = new Set([
  'article',
  'news_overview',
  'news_overview_item',
  'featured_news_overview',
  'featured_news_item',
  'latest_news',
  'related_news',
]);

/** Includes nested blocks and resolved article relations on campaign/office pages. */
export function hasNewsCategories(content: unknown): boolean {
  if (!content || typeof content !== 'object') return false;
  if (Array.isArray(content)) return content.some(hasNewsCategories);
  const block = content as Record<string, unknown>;
  return (
    (typeof block.component === 'string' && newsComponents.has(block.component)) ||
    Object.values(block).some(hasNewsCategories)
  );
}

export function getNewsCategoryLabel(labels: NewsCategoryLabels, value: string): string {
  return Object.prototype.hasOwnProperty.call(labels, value) ? labels[value] || value : value;
}

/** One request per language/space per five minutes, shared by concurrent page renders. */
export async function getNewsCategoryLabels(
  locale?: string,
  preview = false,
): Promise<NewsCategoryLabels> {
  const language = (locale || 'en').toLowerCase();
  // Default English categories are already stored on each article.
  if (language === 'en') return emptyLabels;

  const token = process.env.STORYBLOK_API_KEY;
  if (!token) return emptyLabels;
  const key = `${token}:${language}:${preview}`;
  const cached = cache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.labels;
  const existing = pending.get(key);
  if (existing) return existing;

  const load = async (): Promise<NewsCategoryLabels> => {
    try {
      const entries: DatasourceEntry[] = [];
      const cv = String(Math.floor(Date.now() / 1000));
      for (let page = 1; ; page++) {
        const query = new URLSearchParams({
          token,
          datasource: 'news-categories',
          dimension: language,
          per_page: '100',
          page: String(page),
          cv,
        });
        const response = await fetch(
          `https://api.storyblok.com/v2/cdn/datasource_entries?${query}`,
          {
            cache: 'no-store', // The TTL above owns caching, including preview bypass.
            signal: AbortSignal.timeout(10000),
          },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data: { datasource_entries: DatasourceEntry[] } = await response.json();
        if (!Array.isArray(data.datasource_entries)) throw new Error('Invalid datasource response');
        entries.push(...data.datasource_entries);
        if (data.datasource_entries.length < 100) break;
      }
      const labels = Object.fromEntries(
        entries.map((entry) => [entry.value, entry.dimension_value?.trim() || entry.value]),
      );
      cache.set(key, {
        labels,
        expiresAt: Date.now() + (preview ? 0 : NEWS_CATEGORY_CACHE_SECONDS * 1000),
      });
      return labels;
    } catch {
      // Keep the page usable during a CMS outage; retry on the next request.
      console.warn(
        `Unable to load news category translations for ${language}; using fallback labels.`,
      );
      return cached?.labels || emptyLabels;
    } finally {
      pending.delete(key);
    }
  };
  const result = load();
  pending.set(key, result);
  return result;
}
